import {graphql} from 'react-relay'
import {useFragment} from 'react-relay/hooks'

import {LABELS} from '../constants/labels'
import type {IssueEventRationales$data} from './__generated__/IssueEventRationales.graphql'
import type {IssueFieldAddedEvent$key} from './__generated__/IssueFieldAddedEvent.graphql'
import type {IssueFieldChangedEvent$key} from './__generated__/IssueFieldChangedEvent.graphql'
import type {IssueFieldRemovedEvent$key} from './__generated__/IssueFieldRemovedEvent.graphql'
import {IssueFieldEvent, type IssueFieldEventBaseProps} from './IssueFieldEvent'
import {RolledupIssueFieldEvent} from './RolledupIssueFieldEvent'

type IssueFieldEventKey = IssueFieldAddedEvent$key | IssueFieldChangedEvent$key | IssueFieldRemovedEvent$key

type IssueFieldAddedEventProps = IssueFieldEventBaseProps & {
  queryRef: IssueFieldAddedEvent$key & {createdAt?: string}
  rollupGroup?: Record<string, IssueFieldEventKey[]>
  rationales?: Record<string, IssueEventRationales$data['eventRationales'][number]>
}

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
export const IssueFieldAddedEventFragment = graphql`
  fragment IssueFieldAddedEvent on IssueFieldAddedEvent {
    id
    databaseId
    actor {
      # eslint-disable-next-line relay/must-colocate-fragment-spreads
      ...TimelineRowEventActor
    }
    createdAt
    issueField {
      ... on IssueFieldText {
        dataType
        name
      }
      ... on IssueFieldDate {
        name
        dataType
      }
      ... on IssueFieldNumber {
        name
        dataType
      }
      ... on IssueFieldSingleSelect {
        name
        dataType
      }
    }
    value
    color
  }
`

export function IssueFieldAddedEvent({queryRef, rollupGroup, rationales, ...props}: IssueFieldAddedEventProps) {
  const {
    id,
    actor,
    createdAt,
    issueField,
    value: newValue,
    databaseId,
    color,
  } = useFragment(IssueFieldAddedEventFragment, queryRef)

  const rollupContent = rollupGroup ? (
    <RolledupIssueFieldEvent
      rollupGroup={rollupGroup}
      repositoryNameWithOwner={props.repositoryNameWithOwner}
      rationales={rationales}
    />
  ) : undefined

  return (
    <IssueFieldEvent
      actor={actor}
      createdAt={createdAt}
      fieldName={issueField?.name}
      dataType={issueField?.dataType}
      fieldValue={newValue}
      color={color}
      databaseId={databaseId}
      leadingLabel={rollupGroup ? undefined : LABELS.timeline.issueFieldAdded.leading}
      trailingLabel={rollupGroup ? undefined : LABELS.timeline.issueFieldAdded.trailing}
      hasValue={!rollupGroup}
      rollupContent={rollupContent}
      rationale={rollupGroup ? undefined : rationales?.[id]}
      analyticsAction="ISSUE_FIELD_ADDED"
      {...props}
    />
  )
}
